<template>
	<div class="cassie-vertical-md mt-1">
		<FilterBar
			:search-query.sync="apiAccessKeySearchQuery"
			search-query-label="Name"
			hide-brand-filter
			@persistSearchQuery="changeApiAccessKeySearchQuery"
		>
			<template #action>
				<PrimaryActionButton
					v-if="userFullPermissions"
					@click="$emit('create-access-key', accessKeyType)"
				>
					<v-icon left>
						mdi-plus
					</v-icon>
					Create
				</PrimaryActionButton>
			</template>
		</FilterBar>
		<SectionCard>
			<template #title>
				API Access Keys
			</template>
			<template #body>
				<DataTable
					:headers="tableHeaders"
					:items="filteredApiKeys"
					sort-by="apiKeyId"
					sort-desc
					@click:row="editAccessKey"
				>
					<template #item.action="{ item }">
						<v-menu
							offset-y
							left
						>
							<template #activator="{ on, attrs }">
								<v-btn
									icon
									v-bind="attrs"
									v-on="on"
									@click.stop
								>
									<v-icon>
										mdi-dots-horizontal
									</v-icon>
								</v-btn>
							</template>
							<v-list dense>
								<v-list-item
									@click="copyAccessKey(item.accessKey)"
								>
									<v-list-item-title>
										Copy Key
									</v-list-item-title>
								</v-list-item>
								<v-list-item
									@click="editAccessKey(item)"
								>
									<v-list-item-title>
										{{ userFullPermissions && userHasBrand(item.brandId) ? 'Edit' : 'View' }}
									</v-list-item-title>
								</v-list-item>
								<v-list-item
									v-if="userFullPermissions && userHasBrand(item.brandId)"
									@click="apiKeyToRemove = item"
								>
									<v-list-item-title>
										Delete
									</v-list-item-title>
								</v-list-item>
							</v-list>
						</v-menu>
					</template>
				</DataTable>
			</template>
		</SectionCard>
		<ConfirmDeleteModal
			v-if="apiKeyToRemove"
			:entity-name="apiKeyToRemove.apiKeyName"
			entity-type="API Key"
			@close="apiKeyToRemove = null"
			@delete="removeKey"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import FilterBar from '../../../../../../shared/components/filter-bar.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import ConfirmDeleteModal from '../../../../../../shared/components/confirm-delete-modal.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import { deleteApiKey } from '../../../../../../shared/utils/api/access-keys.js'
import { ACCESS_KEYS_MODULE_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { brandOptions, userHasBrand } from '../../../../../../shared/state/brands.js'
import { changeApiAccessKeySearchQuery, apiAccessKeySearchQuery } from '../../../../../../shared/state/access-keys.js'
import { useLabels } from '../../../../../../shared/state/admin-portal-navigation.js'
export default {
	components: { FilterBar, PrimaryActionButton, SectionCard, DataTable, ConfirmDeleteModal },
	props: {
		accessKeyType: {
			type: Number,
			required: true
		},
		accessKeys: {
			type: Array,
			default: () => []
		}
	},
	setup () {
		return {
			userHasBrand,
			showSnackbar,
			brandOptions,
			changeApiAccessKeySearchQuery,
			apiAccessKeySearchQuery
		}
	},
	data () {
		return {
			searchQuery: null,
			apiKeyToRemove: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		tableHeaders () {
			const tableHeaders = [
				{
					text: 'ID',
					value: 'apiKeyId',
					width: '5%'
				},
				{
					text: useLabels('Brand'),
					value: 'brandName',
					width: '15%'
				},
				{
					text: 'Name',
					value: 'apiKeyName',
					width: '20%'
				},
				{
					text: 'Access Key',
					value: 'apiKey',
					width: '25%'
				},
				{
					text: 'Description',
					value: 'apiKeyDescription',
					width: '25%'
				},
				{
					text: 'Created Date',
					value: 'createdDate',
					width: '10%'
				},
				{
					text: 'Action',
					value: 'action',
					width: '80px',
					align: 'center',
					sortable: false
				}
			]
			return tableHeaders
		},
		filteredApiKeys () {
			return this.accessKeys.filter(({ apiKeyName }) => {
				let check = true
				if (apiAccessKeySearchQuery.value) check = apiKeyName.toLowerCase().includes(apiAccessKeySearchQuery.value.toLowerCase())
				return check
			}).map(apiKey => ({
				...apiKey,
				apiKey: (apiKey.apiKey || '').toUpperCase(),
				brandName: brandOptions.value.find(({ value }) => value === apiKey.brandId)?.text
			}))
		},
		userFullPermissions () {
			return this.productAreaPermission(ACCESS_KEYS_MODULE_FULL_PERMISSIONS)
		}
	},
	methods: {
		editAccessKey (accessKeyToEdit) {
			const accessKeyData = {
				accessKeyToEdit,
				accessKeyType: this.accessKeyType
			}
			this.$emit('edit-access-key', accessKeyData)
		},
		async removeKey () {
			await deleteApiKey(this.apiKeyToRemove.apiKey)
			this.apiKeyToRemove = null
			this.$emit('load-access-keys')
			showSnackbar('This API Key has been removed')
		},
		copyAccessKey (accessKey) {
			navigator.clipboard.writeText((accessKey || '').toUpperCase())
			showSnackbar('Access Key copied to clipboard')
		}
	}
}
</script>
